/**
 * @file src/components/CountdownTimer.tsx
 * Displays a countdown timer showing time remaining until session expiration
 */

import { useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { AccessTime } from '@mui/icons-material'

interface CountdownTimerProps {
  expiresAt: string
}

export const CountdownTimer = ({ expiresAt }: CountdownTimerProps) => {
  console.log('CountdownTimer mounted with expiresAt:', expiresAt) // Debug log
  
  const [timeLeft, setTimeLeft] = useState<{
    hours: number
    minutes: number
    seconds: number
  } | null>(null)

  useEffect(() => {
    const calculateTimeLeft = () => {
      const expirationTime = new Date(expiresAt).getTime()
      const now = new Date().getTime()
      const difference = expirationTime - now
      
      console.log('Time calculation:', { expirationTime, now, difference }) // Debug log

      if (difference <= 0) {
        setTimeLeft(null)
        return
      }

      const hours = Math.floor(difference / (1000 * 60 * 60))
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((difference % (1000 * 60)) / 1000)

      setTimeLeft({ hours, minutes, seconds })
    }

    // Calculate immediately
    calculateTimeLeft()

    // Update every second
    const timer = setInterval(calculateTimeLeft, 1000)

    return () => clearInterval(timer)
  }, [expiresAt])

  if (!timeLeft) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'error.main',
        }}
      >
        <AccessTime fontSize="small" />
        <Typography variant="body2">Session expired</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: timeLeft.hours === 0 && timeLeft.minutes < 30 ? 'warning.main' : 'text.secondary',
      }}
    >
      <AccessTime fontSize="small" />
      <Typography variant="body2">
        Expires in: {timeLeft.hours > 0 ? `${timeLeft.hours}h ` : ''}
        {timeLeft.minutes}m {timeLeft.seconds}s
      </Typography>
    </Box>
  )
} 